import { Supplier } from "../../models/supplier";
import React, { useState, useEffect } from "react";
import Select, { MultiValue, SingleValue } from "react-select";
import { useAppSelector } from "../../reducers/store";

interface Props {
  currentCard: string;
  suppliers: Supplier[];
  setSelectedSuppliers: (value: Supplier[]) => void;
  setSelectedSingleSupplier: (value: Supplier) => void;
  enableAllNotifications: boolean;
  setEnableAllNotifications: (value: boolean) => void;
  isCurrentlySaving: boolean;
}

export default function SupplierSelector(props: Props): JSX.Element {
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const [defaultSuppliers, setDefaultSuppliers] = useState<{ label: string; value: number }[]>([]);

  // Set default suppliers
  useEffect(() => {
    if (props.suppliers?.length && selectedSupplier) {
      const initialData = [
        { label: selectedSupplier?.name, value: selectedSupplier?.id },
      ];
      setDefaultSuppliers(initialData);
      supplierChange(initialData);
      singleSupplierChange(initialData[0]);
    }
  }, [props.suppliers]);

  // Set selected suppliers in parent component on select change
  const supplierChange = (data: MultiValue<any>) => {
    props.setSelectedSuppliers(
      props.suppliers.filter((s) => data.map((d) => d.value).includes(s.id)),
    );
  };

  const singleSupplierChange = (data: SingleValue<any>) => {
    props.setSelectedSingleSupplier(
      props.suppliers.find((s) => s.id === data.value) ?? ({} as Supplier),
    );
  };

  const displayLoader = !props.suppliers?.length && !defaultSuppliers.length;

  return (
    <div className="flex w-full flex-col">
      <div>
        <span className="text-[16px] font-bold text-tqsBlack">Supplier</span>
      </div>
      <div>
        <span className="text-[12px] text-[#8E8E93]">
          {props.currentCard === "notifications"
            ? "Edit your notification settings for each of your associated suppliers"
            : "Edit your team settings for each of your associated suppliers"}
        </span>
      </div>
      {displayLoader ? (
        <div className="flex h-full items-center justify-center">
          <span className="loading loading-spinner loading-lg text-tqsYellow"></span>
        </div>
      ) : (
          <div className="flex flex-row items-center justify-between border-b-[1px] border-b-[rgba(166,166,166,0.3)] pb-[20px]">
            {props.currentCard !== "notifications" &&
            <Select
              isMulti
              name="accountSuppliersMulti"
              options={props.suppliers.map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              }))}
              className="basic-multi-select mt-[8px] h-auto w-[350px] max-w-[350px] text-[black]"
              classNamePrefix="select"
              defaultValue={defaultSuppliers}
              onChange={(value) => supplierChange(value)}
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  height: "auto", // Allow the container to expand
                  maxHeight: "100px", // Limit the height to 100px
                  overflowY: state.hasValue ? "auto" : "visible", // Enable scrolling after reaching 100px
                }),
                menu: (provided) => ({
                  ...provided,
                  color: 'black',
                  zIndex: 9999, // Ensure the dropdown appears above other elements
                }),
                multiValue: (provided) => ({
                  ...provided,
                  color: 'black',
                  maxWidth: "250px", // (Optional) limit the width of selected values
                }),
              }}
            />
            }

            {props.currentCard === "notifications" &&
            <Select
              name="accountSuppliersSingle"
              options={props.suppliers.map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
              }))}
              className="basic-multi-select mt-[8px] h-[36px] min-h-[36px] w-[350px] max-w-[350px]"
              classNamePrefix="select"
              defaultValue={defaultSuppliers[0]}
              isDisabled={props.isCurrentlySaving}
              onChange={(value) => singleSupplierChange(value)}
            />
            }

            {props.currentCard === "notifications" &&
            <div className="ml-[12px] mt-[8px] flex flex-row items-center">
              <input
                type="checkbox"
                checked={props.enableAllNotifications}
                onChange={(event) =>
                  props.setEnableAllNotifications(event.target.checked)
                }
                className="toggle toggle-sm border-black bg-black [--tglbg:#ffffff] checked:[--tglbg:#fedd00] hover:bg-black"
              />
              <span className="ml-[8px] text-[14px] text-tqsBlack">
                Enable All Notifications
              </span>
            </div>
            }
          </div>
      )}  
      
    </div>
  );
}
